import apiClient from "./apiClient";
import { isOfflineMode } from "@/helpers/offlineMode";
import type {
  AllocateAgentRequestModel,
  LoanStatusModel,
  LoanStatusReasonModel,
  LoanStatusTypeModel,
} from "@/models/application";
import type { PrepareApplicationEmployment } from "@/models/underwriting";

// Mirrors getLoanStatusesTypes / getLoanStatusesReasons / moveApplication in the
// twilio-flex-plugin-workscreens application.service.ts, and allocateToAgent in
// its allocate.service.ts.

const mockLoanStatusTypes: LoanStatusTypeModel[] = [
  { statusTypeId: 44, statusType: "Proceeding", allowed: true },
  { statusTypeId: 46, statusType: "Dead", allowed: true },
  { statusTypeId: 48, statusType: "Unable to Contact", allowed: true },
  { statusTypeId: 49, statusType: "Underwriting", allowed: true },
  { statusTypeId: 53, statusType: "On Hold", allowed: true },
];

const mockLoanStatusReasons: LoanStatusReasonModel[] = [
  { statusReasonId: 101, statusReason: "Customer not proceeding", statusTypeId: 46, isActive: true, isActiveForUser: true },
  { statusReasonId: 102, statusReason: "Unable to place with a lender", statusTypeId: 46, isActive: true, isActiveForUser: true },
  { statusReasonId: 103, statusReason: "Bought elsewhere", statusTypeId: 46, isActive: true, isActiveForUser: true },
  { statusReasonId: 104, statusReason: "Duplicate application", statusTypeId: 46, isActive: true, isActiveForUser: true },
];

const getLoanStatusTypes = async (signal?: AbortSignal): Promise<LoanStatusTypeModel[]> => {
  if (isOfflineMode()) return mockLoanStatusTypes;
  const { data } = await apiClient.get<LoanStatusTypeModel[]>("/loan-statuses/types", { signal });
  return data;
};

const getLoanStatusReasons = async (signal?: AbortSignal): Promise<LoanStatusReasonModel[]> => {
  if (isOfflineMode()) return mockLoanStatusReasons;
  const { data } = await apiClient.get<LoanStatusReasonModel[]>("/loan-statuses/reasons", { signal });
  return data;
};

const moveApplication = async (
  loanStatusModel: LoanStatusModel,
  // nextContactDate: Date | null = null
): Promise<void> => {
  if (isOfflineMode()) return;
  await apiClient.post(`/application/${loanStatusModel.applicationId}/move-application/crm-portal`, {
    loanStatusModel,
    nextTouch: null,
  });
};

const allocateToAgent = async (payload: AllocateAgentRequestModel): Promise<void> => {
  if (isOfflineMode()) return;
  await apiClient.post("/twilio-flex-helpers/allocate-to-agent", payload);
};

const updateEmploymentHistory = async (
  applicationId: number,
  employmentHistory: PrepareApplicationEmployment[]
): Promise<void> => {
  if (isOfflineMode()) return;
  await apiClient.patch(`/quote-underwriting/applications/${applicationId}/employment-history`, [
    { op: "replace", path: "/employmentHistory", value: employmentHistory },
  ]);
};

export interface CustomerDetailPatchOperation {
  op: "replace";
  path: string;
  value: unknown;
}

const updateCustomerDetails = async (
  applicationId: number,
  updates: CustomerDetailPatchOperation[]
): Promise<void> => {
  if (isOfflineMode()) return;
  await apiClient.patch(
    `/application/base-application-details/${applicationId}`,
    updates
  );
};

export const applicationService = {
  getLoanStatusTypes,
  getLoanStatusReasons,
  moveApplication,
  allocateToAgent,
  updateEmploymentHistory,
  updateCustomerDetails,
};
